import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { appComponentsStorage } from '@frameio/shared/storage';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@frameio/utils';

const STORAGE_KEY = 'frame-io--recent-searches';
const MAX_RECENT_SEARCHES = 5;

export const useRecentSearches = () => {
  const activeAccountId = useActiveAccountId() || '';
  const storageDataRef = useRef<Record<string, string[]>>(
    EMPTY_OBJECT as Record<string, string[]>
  );
  const notifyRef = useRef<(() => void) | null>(null);

  const getSnapshot = useCallback(() => {
    return storageDataRef.current[activeAccountId] || (EMPTY_ARRAY as string[]);
  }, [activeAccountId]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    notifyRef.current = onStoreChange;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        storageDataRef.current = (appComponentsStorage.get(STORAGE_KEY) ||
          EMPTY_OBJECT) as Record<string, string[]>;
        onStoreChange();
      }
    };

    storageDataRef.current = (appComponentsStorage.get(STORAGE_KEY) ||
      EMPTY_OBJECT) as Record<string, string[]>;

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const recentSearches = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => EMPTY_ARRAY as string[]
  );

  const addRecentSearch = useCallback(
    (searchTerm: string) => {
      const trimmedSearchTerm = searchTerm?.trim();

      if (!activeAccountId || !trimmedSearchTerm) return;

      const allRecentSearches = storageDataRef.current;
      const activeAccountRecentSearches =
        allRecentSearches[activeAccountId] || (EMPTY_ARRAY as string[]);

      if (activeAccountRecentSearches[0]?.trim() === trimmedSearchTerm) return;

      const filteredSearches = activeAccountRecentSearches.filter(
        (term: string) => term.trim() !== trimmedSearchTerm
      );

      const updatedSearches = [trimmedSearchTerm, ...filteredSearches].slice(
        0,
        MAX_RECENT_SEARCHES
      );

      const newStorageData = {
        ...allRecentSearches,
        [activeAccountId]: updatedSearches,
      };

      storageDataRef.current = newStorageData;
      appComponentsStorage.set(STORAGE_KEY, newStorageData);

      if (notifyRef?.current) {
        notifyRef.current();
      }
    },
    [activeAccountId]
  );

  const removeAllRecentSearches = useCallback(() => {
    if (!activeAccountId) return;

    const allRecentSearches = storageDataRef.current;
    const newStorageData = {
      ...allRecentSearches,
      [activeAccountId]: [],
    };

    storageDataRef.current = newStorageData;
    appComponentsStorage.set(STORAGE_KEY, newStorageData);

    if (notifyRef?.current) {
      notifyRef.current();
    }
  }, [activeAccountId]);

  const removeRecentSearch = useCallback(
    (searchTerm: string) => {
      const trimmedSearchTerm = searchTerm?.trim();

      if (!activeAccountId || !trimmedSearchTerm) return;

      const allRecentSearches = storageDataRef.current;
      const activeAccountRecentSearches =
        allRecentSearches[activeAccountId] || (EMPTY_ARRAY as string[]);

      const filteredSearches = activeAccountRecentSearches.filter(
        (term: string) => term.trim() !== trimmedSearchTerm
      );

      const newStorageData = {
        ...allRecentSearches,
        [activeAccountId]: filteredSearches,
      };

      storageDataRef.current = newStorageData;
      appComponentsStorage.set(STORAGE_KEY, newStorageData);

      if (notifyRef?.current) {
        notifyRef.current();
      }
    },
    [activeAccountId]
  );

  return useMemo(
    () => ({
      addRecentSearch,
      recentSearches,
      removeAllRecentSearches,
      removeRecentSearch,
    }),
    [
      addRecentSearch,
      recentSearches,
      removeAllRecentSearches,
      removeRecentSearch,
    ]
  );
};
